import { Box, Dialog } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import React, { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../../contexts/AuthProvider";
import { localStorageService } from "../../../../services/LocalStorageService";
import { INK_BLACK, SURFACE } from "../../../../theme/tokens";
import { trackEvent } from "../../../../utils/analytics";
import { MultiSizePopupHeader } from "./MultiSizePopupHeader";
import { MultiSizePopupState, minimizedViewMaxHeight, minimizedViewMaximumContentWidth, type PreviousStateType } from "./utils";

type Props = {
	title: ReactNode;
	open: boolean;
	bodyRenderer: (currentSizeState: MultiSizePopupState) => ReactNode;
	maxContentWidth?: CSSProperties["maxWidth"];
	/**
	 * Minimized-view max height, in PIXELS.
	 *
	 * Numeric rather than a CSS length: the scroll body below builds `calc(${value}px - 50px)`, so a
	 * string like "50vh" produced `calc(50vhpx - 50px)` — invalid, and it silently killed the body's
	 * max-height and its scrolling.
	 */
	minimizedMaxContentHeight?: number;
	onClose?: (e: React.MouseEvent<HTMLElement>) => void;
	popupLocalStorageKey?: string;
	disableMaximize?: boolean;
	disableMinimize?: boolean;
	closeOnEscape?: boolean;
	disableHide?: boolean;
	"data-testid"?: string;
	/**
	 * If true, the body content will remain mounted in the DOM even when the popup is minimized or hidden.
	 */
	keepBodyMounted?: boolean;
	/** Extra controls (e.g. a reset button) rendered in the header next to the built-in hide/resize/close icons. Only visible when maximized. */
	headerActions?: ReactNode;
};

export const MultiSizePopup = ({
	title,
	open,
	bodyRenderer,
	maxContentWidth,
	minimizedMaxContentHeight,
	onClose,
	popupLocalStorageKey,
	disableMinimize = false,
	disableMaximize = false,
	disableHide = false,
	closeOnEscape = false,
	"data-testid": dataTestId,
	keepBodyMounted = false,
	headerActions,
}: Props) => {
	const {
		userInfo: { id: userId },
	} = useAuth();
	const initialState = useMemo(
		() =>
			popupLocalStorageKey
				? localStorageService.getMultiSizePopupState(popupLocalStorageKey, userId)
				: disableMaximize
					? MultiSizePopupState.MINIMIZED
					: MultiSizePopupState.MAXIMIZED,
		[popupLocalStorageKey, disableMaximize, userId],
	);
	const [sizeState, setSizeState] = useState<MultiSizePopupState>(initialState);
	const [previousSizeState, setPreviousSizeState] = useState<PreviousStateType>(
		initialState !== MultiSizePopupState.HIDDEN ? initialState : MultiSizePopupState.MAXIMIZED,
	);
	const isMax = sizeState === MultiSizePopupState.MAXIMIZED;
	const theme = useTheme();

	const handleSizeStateChange = useCallback(
		(e: React.MouseEvent<HTMLElement>, newSizeState: MultiSizePopupState) => {
			e.stopPropagation();
			if (newSizeState !== MultiSizePopupState.HIDDEN) {
				setPreviousSizeState(newSizeState);
				if (popupLocalStorageKey) {
					localStorageService.setMultiSizePopupState(popupLocalStorageKey, newSizeState, userId);
				}
			}
			setSizeState(newSizeState);
			trackEvent("multi_size_popup_size_change", {
				popupKey: popupLocalStorageKey,
				newSizeState,
			});
		},
		[popupLocalStorageKey, userId],
	);

	useEffect(() => {
		if (!open) {
			return;
		}
		trackEvent("multi_size_popup_opened", {
			popupKey: popupLocalStorageKey,
			initialState,
		});
	}, [open, popupLocalStorageKey, initialState]);

	return (
		<Dialog
			data-testid={dataTestId}
			open={open}
			onClose={(event, reason) => {
				if (closeOnEscape && reason === "escapeKeyDown" && isMax) {
					onClose?.(event as React.MouseEvent<HTMLElement>);
				}
			}}
			hideBackdrop={!isMax}
			disableScrollLock={!isMax}
			slotProps={{
				root: {
					sx: {
						pointerEvents: isMax ? "auto" : "none",
					},
				},
				container: {
					sx: {
						display: "flex",
						width: "100vw",
						height: "100vh",
						maxHeight: "100vh",
						alignItems: "center",
						justifyContent: "center",
						pointerEvents: isMax ? "auto" : "none",
					},
				},
				paper: {
					sx: {
						p: 0,
						m: 0,
						display: "flex",
						width: "100%",
						height: "100%",
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 0,
						background: alpha(INK_BLACK, 0),
						boxShadow: "none",
						maxWidth: "100%",
						maxHeight: "100%",
						overflow: "clip",
						pointerEvents: isMax ? "auto" : "none",
					},
				},
			}}
		>
			<Box
				id={"multi-size-root"}
				key={"multi-size-root"}
				sx={{
					display: "flex",
					alignItems: isMax ? "center" : "end",
					justifyContent: isMax ? "center" : "end",
					height: "100%",
					width: "100%",
					pointerEvents: isMax ? "auto" : "none",
				}}
			>
				<Box
					component={motion.div}
					layout={"position"}
					transition={{
						layout: {
							type: "spring",
							damping: 30,
							stiffness: 400,
						},
					}}
					style={{
						background: SURFACE,
						minWidth: minimizedViewMaximumContentWidth,
						width: "fit-content",
						maxWidth: isMax ? maxContentWidth : minimizedViewMaximumContentWidth,
						maxHeight: !isMax ? (minimizedMaxContentHeight ?? minimizedViewMaxHeight) : "100%",
						height: "fit-content",
						overflowY: "clip",
						borderRadius: isMax ? "8px" : "8px 8px 0px 0px",
						border: 1,
						overflow: "clip",
						boxShadow: `0px 0px 6px 3px ${alpha(theme.palette.primary.main, 0.15)}`,
						pointerEvents: "auto",
					}}
				>
					<Box component={motion.div} layout={false}>
						<MultiSizePopupHeader
							previousState={previousSizeState}
							sizeState={sizeState}
							onClose={(e) => onClose?.(e)}
							title={title}
							onStateChange={handleSizeStateChange}
							disableMinimize={disableMinimize}
							disableMaximize={disableMaximize}
							disableHide={disableHide}
							headerActions={headerActions}
						/>
					</Box>
					<Box
						component={motion.div}
						layout={false}
						sx={{
							width: "100%",
							height: "fit-content",
							maxHeight: `calc(${!isMax ? `${minimizedMaxContentHeight ?? minimizedViewMaxHeight}px` : "100vh"} - 50px)`,
							overflowY: "auto",
						}}
					>
						{(keepBodyMounted || sizeState !== MultiSizePopupState.HIDDEN) && bodyRenderer(sizeState)}
					</Box>
				</Box>
			</Box>
		</Dialog>
	);
};
